import { useState, useEffect, useCallback } from 'react'
import {
  Search,
  Building2,
  RefreshCw,
  Download,
  X,
  ChevronLeft,
  ChevronRight,
  Edit3,
  Check,
  AlertCircle,
  Loader2,
  Filter,
  Shield,
  Plus,
} from 'lucide-react'
import * as Tabs from '@radix-ui/react-tabs'
import { adminService } from '../../services/adminService'

// ─── Types ─────────────────────────────────────────────────────────────────────

interface Business {
  id: string
  name: string
  type: string
  healthScore: number | null
  status: string
  registrationDate?: string
  createdAt?: string
  registrationNumber?: string
}

interface BusinessDetail {
  id: string
  name: string
  type: string
  healthScore: number | null
  status: string
  registrationDate: string
  registrationNumber?: string
  onboardingInputs?: Record<string, unknown>
  apiFetchedData?: Record<string, unknown>
  modelGeneratedData?: Record<string, unknown>
  scoreHistory?: Array<{ score: number; date: string; reason?: string }>
  aiResults?: Array<{ type: string; result: string; date: string; confidence?: number }>
  recommendations?: Array<{ id: string; text: string; priority: string }>
  badges?: Array<{ id: string; name: string; earnedAt?: string; progress?: number }>
  documents?: Array<{ id: string; name: string; type: string; uploadedAt: string }>
  financialRecords?: Array<{ id: string; period: string; revenue?: number; expenses?: number }>
  complianceFilings?: Array<{ id: string; type: string; status: string; filedAt?: string }>
  fields?: Array<{
    key: string
    value: unknown
    source: 'onboarding' | 'api' | 'model' | 'admin'
    adminOverridden?: boolean
    originalValue?: unknown
  }>
}

interface PaginatedResponse {
  items: Business[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

// ─── Component ─────────────────────────────────────────────────────────────────

export function AdminBusinesses() {
  // List state
  const [businesses, setBusinesses] = useState<Business[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [pageSize] = useState(20)
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)

  // Filter state
  const [searchTerm, setSearchTerm] = useState('')
  const [scoreMin, setScoreMin] = useState('')
  const [scoreMax, setScoreMax] = useState('')
  const [filtersOpen, setFiltersOpen] = useState(false)

  // Selection state for bulk actions
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [bulkRecalculating, setBulkRecalculating] = useState(false)

  // Detail state
  const [selectedBusiness, setSelectedBusiness] = useState<BusinessDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState<string | null>(null)

  // Edit state
  const [editingField, setEditingField] = useState<string | null>(null)
  const [editValue, setEditValue] = useState('')
  const [saving, setSaving] = useState(false)

  // Recalculation state
  const [recalculating, setRecalculating] = useState<string | null>(null)

  // Export state
  const [exporting, setExporting] = useState(false)
  const [exportFormat, setExportFormat] = useState<'csv' | 'xlsx'>('csv')
  const [showExportModal, setShowExportModal] = useState(false)

  // Create business state
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)
  const [createForm, setCreateForm] = useState({
    businessName: '',
    pan: '',
    gstin: '',
    businessType: '',
    industrySector: '',
    contactEmail: '',
    contactPhone: '',
    registeredAddress: '',
    annualTurnover: '',
    employeeCount: '',
    ownerEmail: '',
    registrationDate: '',
  })
  const [createValidation, setCreateValidation] = useState<Record<string, string>>({})

  // ─── Data Fetching ─────────────────────────────────────────────────────────

  const fetchBusinesses = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params: Record<string, unknown> = {
        page,
        pageSize,
      }
      if (searchTerm.trim()) params.search = searchTerm.trim()
      if (scoreMin) params.scoreMin = Number(scoreMin)
      if (scoreMax) params.scoreMax = Number(scoreMax)

      const response = await adminService.listBusinesses(params)
      const data = response.data as PaginatedResponse
      setBusinesses(data.items || [])
      setTotal(data.total || 0)
      setTotalPages(data.totalPages || 0)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load businesses'
      setError(message)
    } finally {
      setLoading(false)
    }
  }, [page, pageSize, searchTerm, scoreMin, scoreMax])

  useEffect(() => {
    fetchBusinesses()
  }, [fetchBusinesses])

  const fetchBusinessDetail = useCallback(async (id: string) => {
    setDetailLoading(true)
    setDetailError(null)
    try {
      const response = await adminService.getBusinessDetail(id)
      setSelectedBusiness(response.data as BusinessDetail)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load business details'
      setDetailError(message)
    } finally {
      setDetailLoading(false)
    }
  }, [])

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const handleSearch = useCallback(() => {
    setPage(1)
  }, [])

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') handleSearch()
    },
    [handleSearch]
  )

  const handleApplyFilters = useCallback(() => {
    setPage(1)
    setFiltersOpen(false)
  }, [])

  const handleClearFilters = useCallback(() => {
    setSearchTerm('')
    setScoreMin('')
    setScoreMax('')
    setPage(1)
    setFiltersOpen(false)
  }, [])

  const handleRowClick = useCallback(
    (business: Business) => {
      fetchBusinessDetail(business.id)
    },
    [fetchBusinessDetail]
  )

  const handleCloseDetail = useCallback(() => {
    setSelectedBusiness(null)
    setDetailError(null)
    setEditingField(null)
  }, [])

  const handleSelectToggle = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const handleSelectAll = useCallback(() => {
    if (selectedIds.size === businesses.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(businesses.map((b) => b.id)))
    }
  }, [businesses, selectedIds.size])

  // ─── Field Editing ─────────────────────────────────────────────────────────

  const handleStartEdit = useCallback((key: string, currentValue: unknown) => {
    setEditingField(key)
    setEditValue(String(currentValue ?? ''))
  }, [])

  const handleCancelEdit = useCallback(() => {
    setEditingField(null)
    setEditValue('')
  }, [])

  const handleSaveEdit = useCallback(
    async (fieldKey: string) => {
      if (!selectedBusiness) return
      setSaving(true)
      try {
        await adminService.updateBusinessField(selectedBusiness.id, {
          field: fieldKey,
          value: editValue,
        })
        // Refresh detail
        await fetchBusinessDetail(selectedBusiness.id)
        setEditingField(null)
        setEditValue('')
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to save field'
        setDetailError(message)
      } finally {
        setSaving(false)
      }
    },
    [selectedBusiness, editValue, fetchBusinessDetail]
  )

  // ─── Score Recalculation ───────────────────────────────────────────────────

  const handleRecalculate = useCallback(
    async (id: string) => {
      setRecalculating(id)
      try {
        await adminService.triggerRecalculation(id)
        // Refresh the list and detail if open
        await fetchBusinesses()
        if (selectedBusiness?.id === id) {
          await fetchBusinessDetail(id)
        }
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Recalculation failed'
        setError(message)
      } finally {
        setRecalculating(null)
      }
    },
    [fetchBusinesses, fetchBusinessDetail, selectedBusiness]
  )

  const handleBulkRecalculate = useCallback(async () => {
    if (selectedIds.size === 0) return
    setBulkRecalculating(true)
    try {
      await adminService.triggerBulkRecalculation(Array.from(selectedIds))
      setSelectedIds(new Set())
      await fetchBusinesses()
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Bulk recalculation failed'
      setError(message)
    } finally {
      setBulkRecalculating(false)
    }
  }, [selectedIds, fetchBusinesses])

  // ─── Export ────────────────────────────────────────────────────────────────

  const handleExport = useCallback(async () => {
    if (selectedIds.size === 0 && businesses.length === 0) return
    setExporting(true)
    try {
      const params: Record<string, unknown> = {
        format: exportFormat,
        businessIds: selectedIds.size > 0 ? Array.from(selectedIds) : undefined,
      }
      if (searchTerm.trim()) params.search = searchTerm.trim()
      if (scoreMin) params.scoreMin = Number(scoreMin)
      if (scoreMax) params.scoreMax = Number(scoreMax)

      await adminService.exportBusinessData(params)
      setShowExportModal(false)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Export failed'
      setError(message)
    } finally {
      setExporting(false)
    }
  }, [selectedIds, businesses.length, exportFormat, searchTerm, scoreMin, scoreMax])

  // ─── Create Business ───────────────────────────────────────────────────────

  const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/

  const validateCreateForm = useCallback((): boolean => {
    const errors: Record<string, string> = {}

    if (!createForm.businessName.trim()) {
      errors.businessName = 'Business name is required'
    }
    if (!createForm.pan.trim()) {
      errors.pan = 'PAN is required'
    } else if (!PAN_REGEX.test(createForm.pan.trim().toUpperCase())) {
      errors.pan = 'PAN must be 5 letters + 4 digits + 1 letter (e.g., ABCDE1234F)'
    }
    if (!createForm.businessType) {
      errors.businessType = 'Business type is required'
    }
    if (createForm.contactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(createForm.contactEmail)) {
      errors.contactEmail = 'Invalid email format'
    }
    if (createForm.ownerEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(createForm.ownerEmail)) {
      errors.ownerEmail = 'Invalid email format'
    }

    setCreateValidation(errors)
    return Object.keys(errors).length === 0
  }, [createForm])

  const handleCreateBusiness = useCallback(async () => {
    if (!validateCreateForm()) return

    setCreating(true)
    setCreateError(null)
    try {
      const payload: Record<string, unknown> = {
        businessName: createForm.businessName.trim(),
        pan: createForm.pan.trim().toUpperCase(),
        businessType: createForm.businessType,
      }
      if (createForm.gstin.trim()) payload.gstin = createForm.gstin.trim()
      if (createForm.industrySector.trim()) payload.industrySector = createForm.industrySector.trim()
      if (createForm.contactEmail.trim()) payload.contactEmail = createForm.contactEmail.trim()
      if (createForm.contactPhone.trim()) payload.contactPhone = createForm.contactPhone.trim()
      if (createForm.registeredAddress.trim()) payload.registeredAddress = createForm.registeredAddress.trim()
      if (createForm.annualTurnover) payload.annualTurnover = Number(createForm.annualTurnover)
      if (createForm.employeeCount) payload.employeeCount = Number(createForm.employeeCount)
      if (createForm.ownerEmail.trim()) payload.ownerEmail = createForm.ownerEmail.trim()
      if (createForm.registrationDate) payload.registrationDate = createForm.registrationDate

      await adminService.createBusiness(payload)

      // Reset form and close modal
      setShowCreateModal(false)
      setCreateForm({
        businessName: '',
        pan: '',
        gstin: '',
        businessType: '',
        industrySector: '',
        contactEmail: '',
        contactPhone: '',
        registeredAddress: '',
        annualTurnover: '',
        employeeCount: '',
        ownerEmail: '',
        registrationDate: '',
      })
      setCreateValidation({})

      // Refresh the business list
      await fetchBusinesses()
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosErr = err as { response?: { data?: { error?: { message?: string } }; status?: number } }
        if (axiosErr.response?.status === 409) {
          setCreateError('A business with this PAN already exists')
        } else {
          setCreateError(axiosErr.response?.data?.error?.message || 'Failed to create business')
        }
      } else {
        const message = err instanceof Error ? err.message : 'Failed to create business'
        setCreateError(message)
      }
    } finally {
      setCreating(false)
    }
  }, [createForm, validateCreateForm, fetchBusinesses])

  const handleCreateFormChange = useCallback((field: string, value: string) => {
    setCreateForm((prev) => ({ ...prev, [field]: value }))
    // Clear validation error for this field on change
    setCreateValidation((prev) => {
      const next = { ...prev }
      delete next[field]
      return next
    })
  }, [])

  // ─── Helpers ───────────────────────────────────────────────────────────────

  const getScoreColor = (score: number | null) => {
    if (score === null) return 'text-gray-400 dark:text-gray-500'
    if (score >= 80) return 'text-green-600 dark:text-green-400'
    if (score >= 60) return 'text-yellow-600 dark:text-yellow-400'
    if (score >= 40) return 'text-orange-600 dark:text-orange-400'
    return 'text-red-600 dark:text-red-400'
  }

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      active: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      inactive: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
      suspended: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
      pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
    }
    return styles[status] || styles.inactive
  }

  // ─── Render: Detail Panel ──────────────────────────────────────────────────

  if (selectedBusiness || detailLoading) {
    return (
      <div className="space-y-4">
        {/* Back button */}
        <button
          onClick={handleCloseDetail}
          className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
          Back to Business List
        </button>

        {detailLoading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        )}

        {detailError && (
          <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
            <p className="text-sm text-red-700 dark:text-red-300">{detailError}</p>
          </div>
        )}

        {selectedBusiness && !detailLoading && (
          <div className="space-y-4">
            {/* Header */}
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6">
              <div className="flex items-start justify-between">
                <div>
                  <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {selectedBusiness.name}
                  </h1>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    {selectedBusiness.type} • {selectedBusiness.registrationNumber || 'N/A'} •{' '}
                    <span className={`font-medium ${getStatusBadge(selectedBusiness.status)} px-2 py-0.5 rounded text-xs`}>
                      {selectedBusiness.status}
                    </span>
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleRecalculate(selectedBusiness.id)}
                    disabled={recalculating === selectedBusiness.id}
                    className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <RefreshCw className={`h-4 w-4 ${recalculating === selectedBusiness.id ? 'animate-spin' : ''}`} />
                    Recalculate Score
                  </button>
                </div>
              </div>
              {selectedBusiness.healthScore !== null && (
                <div className="mt-4">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Health Score</span>
                  <p className={`text-3xl font-bold ${getScoreColor(selectedBusiness.healthScore)}`}>
                    {selectedBusiness.healthScore}
                  </p>
                </div>
              )}
            </div>

            {/* Tabbed Detail View */}
            <Tabs.Root defaultValue="basic" className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
              <Tabs.List className="flex border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
                {[
                  { value: 'basic', label: 'Basic Info' },
                  { value: 'scores', label: 'Score History' },
                  { value: 'ai', label: 'AI Results' },
                  { value: 'financial', label: 'Financial Records' },
                  { value: 'compliance', label: 'Compliance' },
                  { value: 'documents', label: 'Documents' },
                  { value: 'badges', label: 'Badges' },
                ].map((tab) => (
                  <Tabs.Trigger
                    key={tab.value}
                    value={tab.value}
                    className="px-4 py-3 text-sm font-medium text-gray-600 dark:text-gray-400 border-b-2 border-transparent hover:text-gray-900 dark:hover:text-white hover:border-gray-300 dark:hover:border-gray-600 data-[state=active]:text-blue-600 data-[state=active]:dark:text-blue-400 data-[state=active]:border-blue-600 data-[state=active]:dark:border-blue-400 whitespace-nowrap transition-colors"
                  >
                    {tab.label}
                  </Tabs.Trigger>
                ))}
              </Tabs.List>

              {/* Basic Info Tab */}
              <Tabs.Content value="basic" className="p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Business Fields</h3>
                {selectedBusiness.fields && selectedBusiness.fields.length > 0 ? (
                  <div className="space-y-3">
                    {selectedBusiness.fields.map((field) => (
                      <div
                        key={field.key}
                        className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700 last:border-0"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300 min-w-[160px]">
                            {field.key}
                          </span>
                          {field.adminOverridden && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                              <Shield className="h-3 w-3" />
                              Admin Override
                            </span>
                          )}
                          <span className="text-xs text-gray-400 dark:text-gray-500">
                            ({field.source})
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          {editingField === field.key ? (
                            <>
                              <input
                                type="text"
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                autoFocus
                              />
                              <button
                                onClick={() => handleSaveEdit(field.key)}
                                disabled={saving}
                                className="p-1 text-green-600 hover:text-green-700 disabled:opacity-50"
                              >
                                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="p-1 text-gray-400 hover:text-gray-600"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </>
                          ) : (
                            <>
                              <span className="text-sm text-gray-900 dark:text-white">
                                {String(field.value ?? '—')}
                              </span>
                              <button
                                onClick={() => handleStartEdit(field.key, field.value)}
                                className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                                title="Edit field"
                              >
                                <Edit3 className="h-4 w-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {Object.entries(selectedBusiness.onboardingInputs || {}).map(([key, value]) => (
                      <div
                        key={key}
                        className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700 last:border-0"
                      >
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{key}</span>
                        <span className="text-sm text-gray-900 dark:text-white">{String(value ?? '—')}</span>
                      </div>
                    ))}
                    {Object.keys(selectedBusiness.onboardingInputs || {}).length === 0 && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">No field data available.</p>
                    )}
                  </div>
                )}
              </Tabs.Content>

              {/* Score History Tab */}
              <Tabs.Content value="scores" className="p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Score History</h3>
                {selectedBusiness.scoreHistory && selectedBusiness.scoreHistory.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200 dark:border-gray-700">
                          <th className="text-left py-2 pr-4 font-medium text-gray-600 dark:text-gray-400">Date</th>
                          <th className="text-left py-2 pr-4 font-medium text-gray-600 dark:text-gray-400">Score</th>
                          <th className="text-left py-2 font-medium text-gray-600 dark:text-gray-400">Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedBusiness.scoreHistory.map((entry, idx) => (
                          <tr key={idx} className="border-b border-gray-100 dark:border-gray-700/50">
                            <td className="py-2 pr-4 text-gray-700 dark:text-gray-300">{entry.date}</td>
                            <td className={`py-2 pr-4 font-medium ${getScoreColor(entry.score)}`}>{entry.score}</td>
                            <td className="py-2 text-gray-600 dark:text-gray-400">{entry.reason || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400">No score history available.</p>
                )}
              </Tabs.Content>

              {/* AI Results Tab */}
              <Tabs.Content value="ai" className="p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">AI Analysis Results</h3>
                {selectedBusiness.aiResults && selectedBusiness.aiResults.length > 0 ? (
                  <div className="space-y-3">
                    {selectedBusiness.aiResults.map((result, idx) => (
                      <div
                        key={idx}
                        className="rounded-lg border border-gray-100 dark:border-gray-700 p-4"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-900 dark:text-white">{result.type}</span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">{result.date}</span>
                        </div>
                        <p className="text-sm text-gray-700 dark:text-gray-300">{result.result}</p>
                        {result.confidence !== undefined && (
                          <div className="mt-2">
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              Confidence: {(result.confidence * 100).toFixed(1)}%
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400">No AI results available.</p>
                )}
              </Tabs.Content>

              {/* Financial Records Tab */}
              <Tabs.Content value="financial" className="p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Financial Records</h3>
                {selectedBusiness.financialRecords && selectedBusiness.financialRecords.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200 dark:border-gray-700">
                          <th className="text-left py-2 pr-4 font-medium text-gray-600 dark:text-gray-400">Period</th>
                          <th className="text-left py-2 pr-4 font-medium text-gray-600 dark:text-gray-400">Revenue</th>
                          <th className="text-left py-2 font-medium text-gray-600 dark:text-gray-400">Expenses</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedBusiness.financialRecords.map((record) => (
                          <tr key={record.id} className="border-b border-gray-100 dark:border-gray-700/50">
                            <td className="py-2 pr-4 text-gray-700 dark:text-gray-300">{record.period}</td>
                            <td className="py-2 pr-4 text-gray-900 dark:text-white">
                              {record.revenue != null ? `$${record.revenue.toLocaleString()}` : '—'}
                            </td>
                            <td className="py-2 text-gray-900 dark:text-white">
                              {record.expenses != null ? `$${record.expenses.toLocaleString()}` : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400">No financial records available.</p>
                )}
              </Tabs.Content>

              {/* Compliance Tab */}
              <Tabs.Content value="compliance" className="p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Compliance Filings</h3>
                {selectedBusiness.complianceFilings && selectedBusiness.complianceFilings.length > 0 ? (
                  <div className="space-y-3">
                    {selectedBusiness.complianceFilings.map((filing) => (
                      <div
                        key={filing.id}
                        className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700 last:border-0"
                      >
                        <div>
                          <span className="text-sm font-medium text-gray-900 dark:text-white">{filing.type}</span>
                          {filing.filedAt && (
                            <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                              Filed: {filing.filedAt}
                            </span>
                          )}
                        </div>
                        <span
                          className={`px-2 py-0.5 rounded text-xs font-medium ${
                            filing.status === 'compliant'
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                              : filing.status === 'pending'
                              ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                              : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                          }`}
                        >
                          {filing.status}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400">No compliance filings available.</p>
                )}
              </Tabs.Content>

              {/* Documents Tab */}
              <Tabs.Content value="documents" className="p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Documents</h3>
                {selectedBusiness.documents && selectedBusiness.documents.length > 0 ? (
                  <div className="space-y-3">
                    {selectedBusiness.documents.map((doc) => (
                      <div
                        key={doc.id}
                        className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700 last:border-0"
                      >
                        <div>
                          <span className="text-sm font-medium text-gray-900 dark:text-white">{doc.name}</span>
                          <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">{doc.type}</span>
                        </div>
                        <span className="text-xs text-gray-500 dark:text-gray-400">{doc.uploadedAt}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400">No documents available.</p>
                )}
              </Tabs.Content>

              {/* Badges Tab */}
              <Tabs.Content value="badges" className="p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Badges</h3>
                {selectedBusiness.badges && selectedBusiness.badges.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {selectedBusiness.badges.map((badge) => (
                      <div
                        key={badge.id}
                        className="rounded-lg border border-gray-200 dark:border-gray-700 p-4"
                      >
                        <h4 className="text-sm font-medium text-gray-900 dark:text-white">{badge.name}</h4>
                        {badge.earnedAt ? (
                          <p className="mt-1 text-xs text-green-600 dark:text-green-400">
                            Earned: {badge.earnedAt}
                          </p>
                        ) : (
                          <div className="mt-2">
                            <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
                              <span>Progress</span>
                              <span>{badge.progress ?? 0}%</span>
                            </div>
                            <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
                              <div
                                className="h-2 bg-blue-600 rounded-full"
                                style={{ width: `${badge.progress ?? 0}%` }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400">No badges available.</p>
                )}
              </Tabs.Content>
            </Tabs.Root>
          </div>
        )}
      </div>
    )
  }

  // ─── Render: List View ─────────────────────────────────────────────────────

  return (
    <div className="space-y-4">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Business Management</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Browse, manage, and export business records
          </p>
        </div>
        <div className="flex items-center gap-2">
          {selectedIds.size > 0 && (
            <button
              onClick={handleBulkRecalculate}
              disabled={bulkRecalculating}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <RefreshCw className={`h-4 w-4 ${bulkRecalculating ? 'animate-spin' : ''}`} />
              Recalculate ({selectedIds.size})
            </button>
          )}
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Create Business
          </button>
          <button
            onClick={() => setShowExportModal(true)}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <Download className="h-4 w-4" />
            Export
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, registration number, or type..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <button
            onClick={handleSearch}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
          >
            <Search className="h-4 w-4" />
            Search
          </button>
          <button
            onClick={() => setFiltersOpen(!filtersOpen)}
            className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
              filtersOpen || scoreMin || scoreMax
                ? 'border-blue-300 dark:border-blue-600 text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/20'
                : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <Filter className="h-4 w-4" />
            Filters
          </button>
        </div>

        {/* Score Range Filter */}
        {filtersOpen && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Score Range
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    placeholder="Min"
                    value={scoreMin}
                    onChange={(e) => setScoreMin(e.target.value)}
                    className="w-20 px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="text-gray-400">—</span>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    placeholder="Max"
                    value={scoreMax}
                    onChange={(e) => setScoreMax(e.target.value)}
                    className="w-20 px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleApplyFilters}
                  className="px-3 py-1.5 text-sm font-medium rounded bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                >
                  Apply
                </button>
                <button
                  onClick={handleClearFilters}
                  className="px-3 py-1.5 text-sm font-medium rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Error State */}
      {error && (
        <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          <button
            onClick={() => setError(null)}
            className="ml-auto p-1 text-red-400 hover:text-red-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Business Table */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : businesses.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Building2 className="h-12 w-12 text-gray-300 dark:text-gray-600 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">No businesses found</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {searchTerm || scoreMin || scoreMax
                ? 'Try adjusting your filters'
                : 'No businesses registered yet'}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    <th className="px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedIds.size === businesses.length && businesses.length > 0}
                        onChange={handleSelectAll}
                        className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Health Score
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Registration Date
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {businesses.map((business) => (
                    <tr
                      key={business.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/30 cursor-pointer transition-colors"
                      onClick={() => handleRowClick(business)}
                    >
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(business.id)}
                          onChange={() => handleSelectToggle(business.id)}
                          className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {business.name}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-600 dark:text-gray-400">{business.type}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-sm font-semibold ${getScoreColor(business.healthScore)}`}>
                          {business.healthScore !== null ? business.healthScore : '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${getStatusBadge(business.status)}`}
                        >
                          {business.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {business.registrationDate || business.createdAt || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => handleRecalculate(business.id)}
                          disabled={recalculating === business.id}
                          className="p-1.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 disabled:opacity-50 transition-colors"
                          title="Recalculate score"
                        >
                          <RefreshCw
                            className={`h-4 w-4 ${recalculating === business.id ? 'animate-spin' : ''}`}
                          />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total} businesses
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-1.5 rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Page {page} of {totalPages || 1}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="p-1.5 rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Export Business Data</h2>
              <button
                onClick={() => setShowExportModal(false)}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Export Format
                </label>
                <div className="flex gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="format"
                      value="csv"
                      checked={exportFormat === 'csv'}
                      onChange={() => setExportFormat('csv')}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">CSV</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="format"
                      value="xlsx"
                      checked={exportFormat === 'xlsx'}
                      onChange={() => setExportFormat('xlsx')}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Excel (XLSX)</span>
                  </label>
                </div>
              </div>

              <div className="text-sm text-gray-600 dark:text-gray-400">
                {selectedIds.size > 0 ? (
                  <p>{selectedIds.size} business(es) selected for export.</p>
                ) : (
                  <p>All businesses matching current filters will be exported.</p>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={() => setShowExportModal(false)}
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleExport}
                  disabled={exporting}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {exporting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                  Export
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Business Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-xl w-full max-w-lg mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Create Business</h2>
              <button
                onClick={() => { setShowCreateModal(false); setCreateError(null); setCreateValidation({}) }}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {createError && (
              <div className="mb-4 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3 flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400 flex-shrink-0" />
                <p className="text-sm text-red-700 dark:text-red-300">{createError}</p>
              </div>
            )}

            <div className="space-y-4">
              {/* Business Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Business Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={createForm.businessName}
                  onChange={(e) => handleCreateFormChange('businessName', e.target.value)}
                  className={`w-full px-3 py-2 text-sm border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    createValidation.businessName ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                  }`}
                  placeholder="Enter business name"
                />
                {createValidation.businessName && (
                  <p className="mt-1 text-xs text-red-600 dark:text-red-400">{createValidation.businessName}</p>
                )}
              </div>

              {/* PAN */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  PAN <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={createForm.pan}
                  onChange={(e) => handleCreateFormChange('pan', e.target.value.toUpperCase())}
                  maxLength={10}
                  className={`w-full px-3 py-2 text-sm border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 uppercase ${
                    createValidation.pan ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                  }`}
                  placeholder="ABCDE1234F"
                />
                {createValidation.pan && (
                  <p className="mt-1 text-xs text-red-600 dark:text-red-400">{createValidation.pan}</p>
                )}
              </div>

              {/* GSTIN */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  GSTIN
                </label>
                <input
                  type="text"
                  value={createForm.gstin}
                  onChange={(e) => handleCreateFormChange('gstin', e.target.value.toUpperCase())}
                  maxLength={15}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 uppercase"
                  placeholder="22ABCDE1234F1Z5"
                />
              </div>

              {/* Business Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Business Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={createForm.businessType}
                  onChange={(e) => handleCreateFormChange('businessType', e.target.value)}
                  className={`w-full px-3 py-2 text-sm border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    createValidation.businessType ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                  }`}
                >
                  <option value="">Select business type</option>
                  <option value="sole_proprietorship">Sole Proprietorship</option>
                  <option value="partnership">Partnership</option>
                  <option value="llp">LLP</option>
                  <option value="private_limited">Private Limited</option>
                  <option value="public_limited">Public Limited</option>
                  <option value="B2B">B2B</option>
                  <option value="ECOMMERCE">E-Commerce</option>
                  <option value="SERVICE">Service</option>
                  <option value="MANUFACTURING">Manufacturing</option>
                  <option value="MSME">MSME</option>
                </select>
                {createValidation.businessType && (
                  <p className="mt-1 text-xs text-red-600 dark:text-red-400">{createValidation.businessType}</p>
                )}
              </div>

              {/* Industry Sector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Industry Sector
                </label>
                <input
                  type="text"
                  value={createForm.industrySector}
                  onChange={(e) => handleCreateFormChange('industrySector', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Manufacturing, IT Services"
                />
              </div>

              {/* Contact Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Contact Email
                </label>
                <input
                  type="email"
                  value={createForm.contactEmail}
                  onChange={(e) => handleCreateFormChange('contactEmail', e.target.value)}
                  className={`w-full px-3 py-2 text-sm border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    createValidation.contactEmail ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                  }`}
                  placeholder="contact@example.com"
                />
                {createValidation.contactEmail && (
                  <p className="mt-1 text-xs text-red-600 dark:text-red-400">{createValidation.contactEmail}</p>
                )}
              </div>

              {/* Contact Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Contact Phone
                </label>
                <input
                  type="text"
                  value={createForm.contactPhone}
                  onChange={(e) => handleCreateFormChange('contactPhone', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="+91 9876543210"
                />
              </div>

              {/* Registered Address */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Registered Address
                </label>
                <textarea
                  value={createForm.registeredAddress}
                  onChange={(e) => handleCreateFormChange('registeredAddress', e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  placeholder="Full registered address"
                />
              </div>

              {/* Annual Turnover */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Annual Turnover (INR)
                </label>
                <input
                  type="number"
                  value={createForm.annualTurnover}
                  onChange={(e) => handleCreateFormChange('annualTurnover', e.target.value)}
                  min="0"
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0"
                />
              </div>

              {/* Employee Count */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Employee Count
                </label>
                <input
                  type="number"
                  value={createForm.employeeCount}
                  onChange={(e) => handleCreateFormChange('employeeCount', e.target.value)}
                  min="0"
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0"
                />
              </div>

              {/* Owner Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Owner Email
                </label>
                <input
                  type="email"
                  value={createForm.ownerEmail}
                  onChange={(e) => handleCreateFormChange('ownerEmail', e.target.value)}
                  className={`w-full px-3 py-2 text-sm border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    createValidation.ownerEmail ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                  }`}
                  placeholder="owner@example.com"
                />
                {createValidation.ownerEmail && (
                  <p className="mt-1 text-xs text-red-600 dark:text-red-400">{createValidation.ownerEmail}</p>
                )}
              </div>

              {/* Registration Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Registration Date
                </label>
                <input
                  type="date"
                  value={createForm.registrationDate}
                  onChange={(e) => handleCreateFormChange('registrationDate', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={() => { setShowCreateModal(false); setCreateError(null); setCreateValidation({}) }}
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateBusiness}
                  disabled={creating}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {creating ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4" />
                  )}
                  Create Business
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
